import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useHistory, useParams } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

import {
  ProgressAiBatch,
  ProgressAiPointResult,
  continueReviewTaskAiBatch,
  getReviewTaskAiBatchPoint,
  listReviewTaskAiBatchPoints,
  rerunReviewTaskAiBatchPoint,
  saveReviewTaskAiBatchPointLesson,
  sendReviewTaskAiBatchPointFeedback,
  startReviewTaskAiBatch,
  stopReviewTaskAiBatch,
  updateReviewTaskAiBatch,
} from '../../../api/reviewTasks'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { blue } from '../../../styles/colors'
import { LoadingIndicator } from '../../common/LoadingIndicator'

import { ProgressAiSuggestionCard } from './ProgressAiSuggestionCard'

const Shell = styled.div`
  height: calc(100vh - 72px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 8px;
`

const Page = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr) 250px;
  gap: 12px;
  padding: 0 16px 16px;
  overflow: hidden;
`

const Panel = styled.section`
  position: relative;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const ImageryPanel = styled(Panel)`
  padding: 8px;
`

const ImageryRow = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 0;
`

const ImageryCard = styled.div<{ $flex?: string }>`
  flex: ${p => p.$flex || '1'};
  min-width: 0;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 4px;
  background: #fff;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const PointButton = styled.button<{ $active?: boolean; $tone?: string }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  border: none;
  background: ${p => (p.$active ? blue : 'transparent')};
  color: ${p => (p.$active ? '#fff' : p.$tone || '#222')};
  padding: 7px 8px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 2px;
  text-align: left;
  font-size: 13px;
`

const PointList = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  margin-top: 4px;
  border-top: 1px solid #eee;
  padding-top: 6px;
`

const TaskTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 17px;
  color: #222;
`

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border: 1px solid ${blue};
  border-radius: 4px;
  background: ${blue};
  color: #fff;
  cursor: pointer;
  padding: 6px 12px;
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const BatchStatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #444;
  margin: 0 0 8px;
  line-height: 1.4;
`

const pulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
`

const RunningDot = styled.span`
  width: 8px;
  height: 8px;
  flex: 0 0 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.22);
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const ControlRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 8px;
`

const IconSquareBtn = styled.button`
  width: 100%;
  aspect-ratio: 1;
  min-height: 36px;
  border: 1px solid ${blue};
  border-radius: 4px;
  background: ${blue};
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const StopIcon = styled.span`
  display: block;
  width: 25%;
  height: 25%;
  min-width: 10px;
  min-height: 10px;
  background: #fff;
  border-radius: 1px;
`

const TaskMeta = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.45;
`

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
`

const NavBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${blue};
  border-radius: 4px;
  background: #fff;
  color: ${blue};
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const NavLabel = styled.b`
  font-size: 14px;
  min-width: 90px;
  text-align: center;
`

const StageBox = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  background: rgb(248, 248, 248);
  border-radius: 4px;
  user-select: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`

const StageHint = styled.div`
  color: #666;
  font-size: 11px;
  text-align: right;
`

const StageNote = styled.div`
  color: #8a6d00;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
`

const StageFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px 10px 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: linear-gradient(
    to top,
    rgba(248, 248, 248, 0.96) 55%,
    rgba(248, 248, 248, 0)
  );
  pointer-events: none;
`

const Meta = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
`

const Button = styled.button`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${blue};
  background: ${blue};
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  margin: 8px 0;
  font-size: 13px;
  box-sizing: border-box;
  resize: vertical;
`

const LessonArea = styled(TextArea)`
  min-height: 56px;
  margin: 4px 0 0;
  flex: 0 0 auto;
`

const CheckboxBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0 0;
  font-size: 12px;
  color: #333;
  flex: 0 0 auto;
`

const ResultsScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 2px;
`

const ResultsFooter = styled.div`
  flex: 0 0 auto;
  border-top: 1px solid #eee;
  padding-top: 10px;
  margin-top: 10px;
`

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 4px;
`

const ActionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const ActionHint = styled.div`
  font-size: 11px;
  color: #777;
  line-height: 1.35;
`

const CompactButton = styled(Button)`
  width: 100%;
  padding: 7px 8px;
  font-size: 12px;
`

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888;
  margin: 0 0 8px;
`

const EmptyState = styled.div`
  font-size: 13px;
  color: #888;
  padding: 8px 0 12px;
`

const NotesBox = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.45;
  white-space: pre-wrap;
  margin-bottom: 4px;
`

const NotesToggle = styled.button`
  border: none;
  background: none;
  color: ${blue};
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  margin-bottom: 4px;
`

const LessonLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #333;
  margin-top: 8px;
  flex: 0 0 auto;
`

const NOTES_WORD_LIMIT = 36

const PROCESSED_POINT_STATUSES = new Set(['succeeded', 'failed', 'skipped'])

const pointListTone = (status: string, suggestionCount: number) => {
  if (status === 'failed') return '#c62828'
  if (status === 'skipped') return '#888'
  if (status === 'succeeded') {
    return suggestionCount > 0 ? '#ed6c02' : '#2e7d32'
  }
  // pending / running / cancelled: unprocessed look
  return '#888'
}

const pointListCount = (status: string, suggestionCount: number) => {
  if (!PROCESSED_POINT_STATUSES.has(status)) return ''
  return String(suggestionCount)
}

const formatTaskDate = (value: string | null | undefined) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const pointLabel = (point: ProgressAiPointResult) =>
  `${point.floor_code} ${point.point_sub_id}`.trim()

const truncateWords = (text: string, limit: number) => {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (words.length <= limit) {
    return { text: text.trim(), truncated: false }
  }
  return { text: `${words.slice(0, limit).join(' ')}…`, truncated: true }
}

type ArtifactImage = {
  data?: string
  url?: string
  media_type?: string
  mediaType?: string
}

const encodedImageUrl = (image?: ArtifactImage | null) => {
  if (!image) return undefined
  if (image.url) return image.url
  if (!image.data) return undefined
  const media = image.mediaType || image.media_type || 'image/jpeg'
  return `data:${media};base64,${image.data}`
}

const mapArtifactsPayload = (artifacts?: {
  zenith?: ArtifactImage
  minimaps?: Array<{ tracker: string; image: ArtifactImage }>
}) => ({
  zenithUrl: encodedImageUrl(artifacts?.zenith),
  minimaps: (artifacts?.minimaps || [])
    .map(entry => ({
      tracker: entry.tracker,
      url: encodedImageUrl(entry.image) || '',
    }))
    .filter(entry => entry.url),
})

const TERMINAL_POINT_STATUSES = new Set([
  'succeeded',
  'failed',
  'skipped',
  'cancelled',
])

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, ms)
  })

const pollPointUntilTerminal = async (
  projectId: string,
  taskId: number,
  pointResultId: number,
  {
    includeArtifacts = false,
    timeoutMs = 10 * 60 * 1000,
    intervalMs = 2000,
  }: {
    includeArtifacts?: boolean
    timeoutMs?: number
    intervalMs?: number
  } = {},
) => {
  const started = Date.now()
  for (;;) {
    const result = await getReviewTaskAiBatchPoint(
      projectId,
      taskId,
      pointResultId,
      { includeArtifacts },
    )
    if (TERMINAL_POINT_STATUSES.has(result.point.status)) {
      return result
    }
    if (Date.now() - started > timeoutMs) {
      throw new Error('Timed out waiting for AI point to finish')
    }
    await sleep(intervalMs)
  }
}

/** Independent pan/zoom viewport (matches tracker-verify-ui stage). */
function PanZoom({
  contentWidth,
  note,
  maxFit = 1.4,
  children,
}: {
  contentWidth: number
  note?: string
  maxFit?: number
  children: ReactNode
}) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState({ scale: 0.5, tx: 10, ty: 10 })
  const drag = useRef<{ x: number; y: number } | null>(null)

  const fit = useCallback(() => {
    const el = boxRef.current
    if (!el) return
    const s = Math.min((el.clientWidth - 20) / contentWidth, maxFit)
    setView({ scale: s, tx: 10, ty: 10 })
  }, [contentWidth, maxFit])

  useEffect(() => {
    fit()
  }, [fit, contentWidth])

  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const my = e.clientY - rect.top
      setView(v => {
        const ns = Math.min(
          8,
          Math.max(0.1, v.scale * Math.exp(-e.deltaY * 0.0015)),
        )
        return {
          scale: ns,
          tx: mx - ((mx - v.tx) / v.scale) * ns,
          ty: my - ((my - v.ty) / v.scale) * ns,
        }
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  return (
    <StageBox
      ref={boxRef}
      onMouseDown={e => {
        if (e.button === 0) {
          e.preventDefault()
          drag.current = { x: e.clientX, y: e.clientY }
        }
      }}
      onMouseMove={e => {
        if (!drag.current) return
        const d = drag.current
        setView(v => ({
          ...v,
          tx: v.tx + e.clientX - d.x,
          ty: v.ty + e.clientY - d.y,
        }))
        drag.current = { x: e.clientX, y: e.clientY }
      }}
      onMouseUp={() => {
        drag.current = null
      }}
      onMouseLeave={() => {
        drag.current = null
      }}
      onDoubleClick={fit}
    >
      <div
        style={{
          transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
          transformOrigin: '0 0',
          width: 'fit-content',
        }}
      >
        {children}
      </div>
      <StageFooter>
        {note ? <StageNote>{note}</StageNote> : null}
        <StageHint>scroll = zoom · drag = pan · double-click = fit</StageHint>
      </StageFooter>
    </StageBox>
  )
}

export function ReviewAiBatchReviewPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const history = useHistory()
  const { generateAdminManageReviewPath } = useGeneratedPaths()
  const { state: buildingState } = useBuildingContext()
  const projectId = buildingState.projectId
  const { addNotification } = useNotifications()

  const [points, setPoints] = useState<ProgressAiPointResult[]>([])
  const [batch, setBatch] = useState<ProgressAiBatch | null>(null)
  const [taskMeta, setTaskMeta] = useState<{
    floors_label: string
    registered_on: string | null
  } | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<ProgressAiPointResult | null>(null)
  const [artifacts, setArtifacts] = useState<{
    zenithUrl?: string
    minimaps: Array<{ tracker: string; url: string }>
  }>({ minimaps: [] })
  const [lessons, setLessons] = useState('')
  const [feedback, setFeedback] = useState('')
  const [verifyInstalled, setVerifyInstalled] = useState(false)
  const [sendExamples, setSendExamples] = useState(true)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [canSaveLesson, setCanSaveLesson] = useState(false)

  const selectedIndex = useMemo(
    () => points.findIndex(point => point.id === selectedId),
    [points, selectedId],
  )
  const selectedPoint = selectedIndex >= 0 ? points[selectedIndex] : undefined
  const batchRunning =
    batch?.status === 'running' || batch?.status === 'not_started'
  const batchStatusLabel = useMemo(() => {
    if (!batch) return { running: false, text: 'No batch yet' }
    if (batch.status === 'running' || batch.status === 'not_started') {
      return { running: true, text: 'Running' }
    }
    const finished =
      (Number(batch.points_done) || 0) +
      (Number(batch.points_failed) || 0) +
      (Number(batch.points_skipped) || 0)
    const total = Number(batch.points_total) || 0
    const cost = `$${(Number(batch.cost_usd) || 0).toFixed(2)}`
    return {
      running: false,
      text: `${finished}/${total} ${batch.status} · ${cost}`,
    }
  }, [batch])

  const markBatchRunning = () => {
    setBatch(prev =>
      prev
        ? { ...prev, status: 'running', completed_at: null }
        : prev,
    )
  }

  const loadPoints = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!projectId || !taskId) return
      if (!opts?.quiet) setLoading(true)
      try {
        const result = await listReviewTaskAiBatchPoints(
          projectId,
          Number(taskId),
        )
        setPoints(result.points || [])
        if (result.batch) {
          setBatch(result.batch)
        } else if (!opts?.quiet) {
          setBatch(null)
        }
        if (!opts?.quiet) {
          const nextLessons =
            result.lessons ?? result.batch?.lessons ?? ''
          setLessons(nextLessons)
          if (result.batch) {
            setVerifyInstalled(Boolean(result.batch.verify_installed))
            setSendExamples(Boolean(result.batch.send_examples))
          }
        }
        if (result.task) {
          setTaskMeta({
            floors_label: result.task.floors_label,
            registered_on: result.task.registered_on,
          })
        } else {
          const floors = Array.from(
            new Set(
              (result.points || [])
                .map(point => point.floor_code)
                .filter(Boolean),
            ),
          )
          setTaskMeta({
            floors_label: floors.length ? floors.join(', ') : '—',
            registered_on: null,
          })
        }
        setSelectedId(prev => {
          if (prev && result.points?.some(point => point.id === prev))
            return prev
          return result.points?.[0]?.id ?? null
        })
      } catch (err: unknown) {
        const message =
          (
            err as {
              response?: { data?: { detail?: string } }
              message?: string
            }
          )?.response?.data?.detail ||
          (err as { message?: string })?.message ||
          'Failed to load AI batch points'
        addNotification(message, 'error')
      } finally {
        if (!opts?.quiet) setLoading(false)
      }
    },
    [addNotification, projectId, taskId],
  )

  useEffect(() => {
    void loadPoints()
  }, [loadPoints])

  useEffect(() => {
    if (!batchRunning) return undefined
    const id = window.setInterval(() => {
      void loadPoints({ quiet: true })
    }, 2000)
    return () => window.clearInterval(id)
  }, [batchRunning, loadPoints])

  useEffect(() => {
    setNotesExpanded(false)
    setFeedback('')
    setCanSaveLesson(false)
    setDetail(null)
    setArtifacts({ minimaps: [] })
  }, [selectedId])

  const selectedStatus = selectedPoint?.status ?? null

  useEffect(() => {
    if (!projectId || !taskId || !selectedId) return
    let cancelled = false
    const includeArtifacts = selectedStatus === 'succeeded'
    const run = async () => {
      try {
        const result = await getReviewTaskAiBatchPoint(
          projectId,
          Number(taskId),
          selectedId,
          { includeArtifacts },
        )
        if (cancelled) return
        setDetail(result.point)
        if (includeArtifacts) {
          setArtifacts(mapArtifactsPayload(result.artifacts))
          if (result.artifacts_error) {
            addNotification(result.artifacts_error, 'warning')
          }
        } else {
          setArtifacts({ minimaps: [] })
        }
      } catch (err: unknown) {
        if (cancelled) return
        // Unprocessed points should not surface hard errors — show empty states.
        if (!includeArtifacts) {
          setDetail(null)
          setArtifacts({ minimaps: [] })
          return
        }
        const message =
          (
            err as {
              response?: { data?: { detail?: string } }
              message?: string
            }
          )?.response?.data?.detail ||
          (err as { message?: string })?.message ||
          'Failed to load point detail'
        addNotification(message, 'error')
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [addNotification, projectId, selectedId, selectedStatus, taskId])

  const { highSuggestions, mediumSuggestions, lowSuggestions } = useMemo(() => {
    const all = detail?.suggestions || []
    const high = all.filter(s => (s.confidence || '').toLowerCase() === 'high')
    const medium = all.filter(
      s => (s.confidence || '').toLowerCase() === 'medium',
    )
    const low = all.filter(s => {
      const level = (s.confidence || '').toLowerCase()
      return level !== 'high' && level !== 'medium'
    })
    return {
      highSuggestions: high,
      mediumSuggestions: medium,
      lowSuggestions: low,
    }
  }, [detail])

  const notesSnippet = useMemo(() => {
    const notes = (detail?.notes || '').trim()
    if (!notes) return null
    return truncateWords(notes, NOTES_WORD_LIMIT)
  }, [detail])

  const goPrev = () => {
    if (selectedIndex <= 0) return
    setSelectedId(points[selectedIndex - 1].id)
  }

  const goNext = () => {
    if (selectedIndex < 0 || selectedIndex >= points.length - 1) return
    setSelectedId(points[selectedIndex + 1].id)
  }

  const goNextFlagged = () => {
    if (selectedIndex < 0) return
    const next = points.findIndex(
      (point, index) =>
        index > selectedIndex && (point.suggestions?.length || 0) > 0,
    )
    if (next >= 0) setSelectedId(points[next].id)
    else if (selectedIndex < points.length - 1) {
      setSelectedId(points[points.length - 1].id)
    }
  }

  const persistOptions = async () => {
    if (!projectId || !taskId) return
    await updateReviewTaskAiBatch(projectId, Number(taskId), {
      verify_installed: verifyInstalled,
      send_examples: sendExamples,
      lessons,
    })
  }

  const onStartFromActive = async () => {
    if (!projectId || !taskId || !selectedPoint) return
    if (batchRunning) {
      addNotification(
        'Stop the running batch before starting from the active point.',
        'error',
      )
      return
    }
    setBusy(true)
    markBatchRunning()
    if (selectedIndex >= 0) {
      setPoints(prev =>
        prev.map((point, index) =>
          index >= selectedIndex
            ? {
                ...point,
                status: 'pending',
                suggestions: [],
                notes: '',
                error: null,
              }
            : point,
        ),
      )
    }
    try {
      if (batch) {
        await persistOptions()
        // Resume on the same batch so already-processed points stay visible.
        const result = await continueReviewTaskAiBatch(
          projectId,
          Number(taskId),
          { from_point_sub_id: selectedPoint.point_sub_id },
        )
        setBatch(result.batch)
      } else {
        // No prior batch: create one starting at the active point.
        const result = await startReviewTaskAiBatch(projectId, Number(taskId), {
          verify_installed: verifyInstalled,
          send_examples: sendExamples,
          lessons,
          from_point_sub_id: selectedPoint.point_sub_id,
        })
        setBatch(result.batch)
      }
      addNotification('Started from active point', 'success')
      await loadPoints({ quiet: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to start from active point'
      addNotification(message, 'error')
      await loadPoints({ quiet: true })
    } finally {
      setBusy(false)
    }
  }

  const onContinue = async () => {
    if (!projectId || !taskId) return
    const hasUnprocessed = points.some(
      point =>
        point.status === 'pending' ||
        point.status === 'cancelled' ||
        point.status === 'failed',
    )
    const startFresh = Boolean(batch) && !hasUnprocessed && !batchRunning
    if (startFresh) {
      const ok = window.confirm(
        'No unprocessed points found. Start a new batch from the first point? This creates a new batch (prior batch results remain in history).',
      )
      if (!ok) return
    }
    setBusy(true)
    markBatchRunning()
    try {
      if (batch) await persistOptions()
      if (!batch || startFresh) {
        const result = await startReviewTaskAiBatch(projectId, Number(taskId), {
          verify_installed: verifyInstalled,
          send_examples: sendExamples,
          lessons,
        })
        setBatch(result.batch)
      } else {
        const result = await continueReviewTaskAiBatch(
          projectId,
          Number(taskId),
        )
        setBatch(result.batch)
      }
      addNotification('Batch continued', 'success')
      await loadPoints({ quiet: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to continue batch'
      addNotification(message, 'error')
      await loadPoints({ quiet: true })
    } finally {
      setBusy(false)
    }
  }

  const onStop = async () => {
    if (!projectId || !taskId) return
    setBusy(true)
    try {
      const result = await stopReviewTaskAiBatch(projectId, Number(taskId))
      setBatch(result.batch)
      addNotification('Batch stopped', 'success')
      await loadPoints({ quiet: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to stop batch'
      addNotification(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const onRunOne = async () => {
    if (!projectId || !taskId || !selectedId || !selectedPoint) return
    setBusy(true)
    markBatchRunning()
    setPoints(prev =>
      prev.map(point =>
        point.id === selectedId
          ? {
              ...point,
              status: 'pending',
              suggestions: [],
              notes: '',
              error: null,
            }
          : point,
      ),
    )
    try {
      if (batch) {
        await persistOptions()
        await rerunReviewTaskAiBatchPoint(projectId, Number(taskId), selectedId)
      } else {
        await startReviewTaskAiBatch(projectId, Number(taskId), {
          verify_installed: verifyInstalled,
          send_examples: sendExamples,
          lessons,
          only_point_sub_id: selectedPoint.point_sub_id,
        })
      }
      addNotification('Running one point…', 'success')
      const refreshed = await pollPointUntilTerminal(
        projectId,
        Number(taskId),
        selectedId,
        { includeArtifacts: true },
      )
      setDetail(refreshed.point)
      setArtifacts(mapArtifactsPayload(refreshed.artifacts))
      await loadPoints({ quiet: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Run one point failed'
      addNotification(message, 'error')
      await loadPoints({ quiet: true })
    } finally {
      setBusy(false)
    }
  }

  const onSaveLesson = async () => {
    if (!projectId || !taskId || !selectedId || !canSaveLesson) return
    setBusy(true)
    try {
      const result = await saveReviewTaskAiBatchPointLesson(
        projectId,
        Number(taskId),
        selectedId,
        feedback.trim(),
      )
      setLessons(result.batch.lessons || '')
      setFeedback('')
      setCanSaveLesson(false)
      addNotification(
        result.lesson_line
          ? `Saved lesson: ${result.lesson_line}`
          : 'Saved as lesson',
        'success',
      )
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to save lesson'
      addNotification(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const onSendFeedback = async () => {
    if (!projectId || !taskId || !selectedId || !feedback.trim()) return
    if (detail?.status !== 'succeeded' && !(detail?.raw || '').trim()) {
      addNotification(
        'Send feedback requires a completed judgment on this point.',
        'error',
      )
      return
    }
    setBusy(true)
    setCanSaveLesson(false)
    const comment = feedback.trim()
    try {
      await sendReviewTaskAiBatchPointFeedback(
        projectId,
        Number(taskId),
        selectedId,
        comment,
      )
      const refreshed = await pollPointUntilTerminal(
        projectId,
        Number(taskId),
        selectedId,
        { includeArtifacts: true },
      )
      setDetail(refreshed.point)
      setArtifacts(mapArtifactsPayload(refreshed.artifacts))
      setCanSaveLesson(true)
      addNotification('Re-judged with feedback', 'success')
      await loadPoints({ quiet: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Feedback re-judge failed'
      addNotification(message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const onPersistLessons = async () => {
    if (!projectId || !taskId) return
    try {
      const result = await updateReviewTaskAiBatch(projectId, Number(taskId), {
        lessons,
        verify_installed: verifyInstalled,
        send_examples: sendExamples,
      })
      if (result.lessons != null) {
        setLessons(result.lessons)
      } else if (result.batch) {
        setLessons(result.batch.lessons || '')
      }
      if (result.batch) setBatch(result.batch)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to save lessons'
      addNotification(message, 'error')
    }
  }

  if (!projectId) return <LoadingIndicator />
  if (loading && points.length === 0 && !taskMeta) return <LoadingIndicator />

  return (
    <Shell>
      <Page>
        <Panel>
          <BackLink
            type="button"
            onClick={() =>
              history.push(
                `${generateAdminManageReviewPath(projectId)}?tab=ai_assist`,
              )
            }
          >
            ← Back
          </BackLink>
          <TaskTitle>Task {taskId}</TaskTitle>
          <TaskMeta>
            <div>Levels: {taskMeta?.floors_label || '—'}</div>
            <div>Created: {formatTaskDate(taskMeta?.registered_on)}</div>
          </TaskMeta>
          <BatchStatusLine>
            {batchStatusLabel.running ? <RunningDot aria-hidden /> : null}
            {batchStatusLabel.text}
          </BatchStatusLine>

          <NavRow>
            <NavBtn
              type="button"
              aria-label="Previous viewpoint"
              disabled={selectedIndex <= 0}
              onClick={goPrev}
            >
              ‹
            </NavBtn>
            <NavLabel>
              {selectedPoint ? pointLabel(selectedPoint) : '—'}
            </NavLabel>
            <NavBtn
              type="button"
              aria-label="Next viewpoint"
              disabled={selectedIndex < 0 || selectedIndex >= points.length - 1}
              onClick={goNext}
            >
              ›
            </NavBtn>
            <NavBtn
              type="button"
              aria-label="Next flagged viewpoint"
              title="Jump to next viewpoint with suggestions"
              disabled={selectedIndex < 0 || selectedIndex >= points.length - 1}
              onClick={goNextFlagged}
            >
              »
            </NavBtn>
          </NavRow>

          <ControlRow>
            <IconSquareBtn
              type="button"
              title="Start from active point"
              disabled={busy || !selectedPoint || batchRunning}
              onClick={() => void onStartFromActive()}
            >
              ▶
            </IconSquareBtn>
            <IconSquareBtn
              type="button"
              title="Continue from first unprocessed"
              disabled={busy || batchRunning}
              onClick={() => void onContinue()}
            >
              ⏭
            </IconSquareBtn>
            <IconSquareBtn
              type="button"
              title="Stop batch"
              disabled={busy || !batchRunning}
              onClick={() => void onStop()}
            >
              <StopIcon aria-hidden />
            </IconSquareBtn>
            <IconSquareBtn
              type="button"
              title="Run one point (current)"
              disabled={busy || !selectedId || batchRunning}
              onClick={() => void onRunOne()}
            >
              ①
            </IconSquareBtn>
          </ControlRow>

          <CheckboxBlock>
            <label>
              <input
                type="checkbox"
                checked={verifyInstalled}
                onChange={e => setVerifyInstalled(e.target.checked)}
              />{' '}
              also verify installed (1→0)
            </label>
            <label>
              <input
                type="checkbox"
                checked={sendExamples}
                onChange={e => setSendExamples(e.target.checked)}
              />{' '}
              send reference examples
            </label>
          </CheckboxBlock>

          <PointList>
            {points.map(point => (
              <PointButton
                key={point.id}
                type="button"
                $active={point.id === selectedId}
                $tone={pointListTone(
                  point.status,
                  point.suggestions?.length || 0,
                )}
                onClick={() => setSelectedId(point.id)}
              >
                <span>{pointLabel(point)}</span>
                <span>
                  {pointListCount(point.status, point.suggestions?.length || 0)}
                </span>
              </PointButton>
            ))}
          </PointList>

          <LessonLabel>Lesson</LessonLabel>
          <LessonArea
            value={lessons}
            onChange={e => setLessons(e.target.value)}
            onBlur={() => void onPersistLessons()}
            placeholder="Lessons sent with every judgment (one rule per line)"
          />
        </Panel>

        <ImageryPanel>
          <ImageryRow>
            <ImageryCard $flex="1.55 1 0">
              {artifacts.zenithUrl ? (
                <PanZoom
                  key={`zenith-${selectedId}`}
                  contentWidth={920}
                  note="plan-oriented view (matches minimap directions) — wall text appears mirrored by design"
                >
                  <img
                    src={artifacts.zenithUrl}
                    width={900}
                    style={{ display: 'block', paddingBottom: 28 }}
                    draggable={false}
                    alt="zenith"
                  />
                </PanZoom>
              ) : (
                <Meta style={{ padding: 12 }}>No zenith artifact</Meta>
              )}
            </ImageryCard>
            <ImageryCard $flex="1.15 1 0">
              {artifacts.minimaps.length > 0 ? (
                <PanZoom
                  key={`maps-${selectedId}`}
                  contentWidth={380}
                  maxFit={1.75}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    {artifacts.minimaps.map(minimap => (
                      <img
                        key={minimap.tracker}
                        src={minimap.url}
                        width={360}
                        draggable={false}
                        alt={minimap.tracker}
                        title={minimap.tracker}
                      />
                    ))}
                  </div>
                </PanZoom>
              ) : (
                <Meta style={{ padding: 12 }}>No floor plan artifacts</Meta>
              )}
            </ImageryCard>
          </ImageryRow>
        </ImageryPanel>

        <Panel>
          <ResultsScroll>
            <SectionLabel>High confidence</SectionLabel>
            {highSuggestions.length === 0 ? (
              <EmptyState>
                {detail?.status === 'succeeded'
                  ? 'No high-confidence changes'
                  : 'No suggestions yet'}
              </EmptyState>
            ) : (
              highSuggestions.map(suggestion => (
                <ProgressAiSuggestionCard
                  key={`high-${suggestion.item_id}-${suggestion.job_type}-${suggestion.change}`}
                  suggestion={suggestion}
                />
              ))
            )}

            {mediumSuggestions.length > 0 && (
              <>
                <SectionLabel style={{ marginTop: 12 }}>
                  Medium confidence
                </SectionLabel>
                {mediumSuggestions.map(suggestion => (
                  <ProgressAiSuggestionCard
                    key={`medium-${suggestion.item_id}-${suggestion.job_type}-${suggestion.change}`}
                    suggestion={suggestion}
                  />
                ))}
              </>
            )}

            {lowSuggestions.length > 0 && (
              <>
                <SectionLabel style={{ marginTop: 12 }}>
                  Low confidence
                </SectionLabel>
                {lowSuggestions.map(suggestion => (
                  <ProgressAiSuggestionCard
                    key={`low-${suggestion.item_id}-${suggestion.job_type}-${suggestion.change}`}
                    suggestion={suggestion}
                  />
                ))}
              </>
            )}

            <SectionLabel style={{ marginTop: 12 }}>Notes</SectionLabel>
            {!notesSnippet ? (
              <EmptyState>—</EmptyState>
            ) : (
              <>
                <NotesBox>
                  {notesExpanded ? detail?.notes : notesSnippet.text}
                </NotesBox>
                {notesSnippet.truncated && (
                  <NotesToggle
                    type="button"
                    onClick={() => setNotesExpanded(prev => !prev)}
                  >
                    {notesExpanded ? 'Show less' : 'Read full notes'}
                  </NotesToggle>
                )}
              </>
            )}
          </ResultsScroll>

          <ResultsFooter>
            <SectionLabel>Feedback</SectionLabel>
            <TextArea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Admin feedback for this judgment"
            />
            <ActionRow>
              <ActionCell>
                <CompactButton
                  type="button"
                  disabled={
                    busy ||
                    !feedback.trim() ||
                    !selectedId ||
                    (detail?.status !== 'succeeded' &&
                      !(detail?.raw || '').trim())
                  }
                  onClick={() => void onSendFeedback()}
                >
                  Send feedback
                </CompactButton>
                <ActionHint>
                  Re-judges this viewpoint using your comment in the
                  conversation.
                </ActionHint>
              </ActionCell>
              <ActionCell>
                <CompactButton
                  type="button"
                  disabled={busy || !selectedId || !canSaveLesson}
                  onClick={() => void onSaveLesson()}
                >
                  Save lesson
                </CompactButton>
                <ActionHint>
                  Active after Send feedback succeeds. Distills a reusable rule
                  into Lesson.
                </ActionHint>
              </ActionCell>
            </ActionRow>
          </ResultsFooter>
        </Panel>
      </Page>
    </Shell>
  )
}
